// CategorySales.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Text,
  Icon,
  Flex,
  Spinner,
  VStack,
  HStack,
  SimpleGrid,
  useColorModeValue,
  Heading,
  IconButton,
  Image,
  useBreakpointValue,
  Button,
  Skeleton,
} from "@chakra-ui/react";
import { FiTrendingUp, FiTrendingDown, FiArrowLeft } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";

// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Import Swiper modules
import { Navigation, Pagination, Scrollbar, A11y } from "swiper/modules";
import API, { getMediaURL } from "../../context/API";
import DateRangePicker from "./DateRangePicker";
import DateSelector from "./DateSelector";
import ChartSelection from "./ChartSelection";
import BarLineChart from "./BarLineChart";
import moment from "moment";
import KeyMetricsSelector from "./KeyMetricSelector";
import CustomersDropDown from "../Loyalty/CutomersDropDown";
import StoresDropDown from "./StoresDropDown";
import { motion } from "framer-motion";

// Reusable SalesSummary Component tailored for Category Sales
const addCommasInPrices = (value) => {
  return parseFloat(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}; 
const CategorySalesSummary = ({ weeklyChange, borderColor, merchant, storeId }) => {
  // Determine the trend direction and color
  const isIncrease = weeklyChange >= 0;
  const trendIcon = isIncrease ? FiTrendingUp : FiTrendingDown;
  const trendColor = isIncrease ? "green.400" : "red.400";
  const [isLoading, setIsLoading] = useState(true);
  // Chakra UI color modes
  const textColor = useColorModeValue("gray.800", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.500");

  const [selectedFilter, setSelectedFilter] = useState("allTime");
  const [keyMetrics, setKeyMetrics] = useState([]);
  const [totalSales, setTotalSales] = useState("0.00$");
  const [totalCategories, setTotalCategories] = useState(" - ");
  
  const [selectedDateRange, setSelectedDateRange] = useState({
      startDate: moment("2023-01-01"), // Start date is 1st January 2000
      endDate: moment(), // End date is the current date
    });

  const handleSelection = (selection) => {
    setSelectedDateRange(selection);
  };

  const fetchAnalyticsData = async () => {
    try {
      const params1 = {
        startDate:moment(selectedDateRange.startDate)?.format("YYYY-MM-DD"),
        endDate: moment(selectedDateRange.endDate)?.format("YYYY-MM-DD"),
        merchantId: merchant?.id,
        store:storeId
      };
      const token = localStorage.getItem("access_token");
      const res = await API.get("/analytics/category-sales", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params1,
        merchantId: merchant?.id,
      });

      const categoriesData = res.data.data.categories;
      setTotalCategories(categoriesData.length);
      const totalRevenue = categoriesData.reduce(
        (sum, category) => sum + parseFloat(category.totalRevenue),
        0
      );
      setTotalSales(totalRevenue);

      // Calculate average revenue per category
      const averageRevenue = totalRevenue / categoriesData.length || 0;

      // Find best category by revenue
      const bestCategoryByRevenue = categoriesData.reduce((prev, current) =>
        parseFloat(current.totalRevenue) > parseFloat(prev.totalRevenue)
          ? current
          : prev
      );

      // Find best category by units sold
      const bestCategoryByUnitsSold = categoriesData.reduce((prev, current) =>
        current.totalItemsSold > prev.totalItemsSold ? current : prev
      );

      // Set key metrics
      setKeyMetrics([
        {
          id: "totalSales",
          title: "Total Sales",
          subTitle: "Overall revenue from all categories",
          value: `$${addCommasInPrices(totalRevenue.toFixed(2))}`,
          icon: FiTrendingUp,
          color: "green.500",
        },
        {
          id: "bestCategoryRevenue",
          title: "Best Category by Revenue",
          subTitle: bestCategoryByRevenue.category || "-",
          value: `$${addCommasInPrices(
            parseFloat(bestCategoryByRevenue.totalRevenue).toFixed(2)
          )}`,
          icon: FiTrendingUp,
          color: "blue.500",
        },
        {
          id: "bestCategoryUnits",
          title: "Best Category by Units Sold",
          subTitle: bestCategoryByUnitsSold.category || "-",
          value: `${bestCategoryByUnitsSold.totalItemsSold} Units`,
          icon: FiTrendingUp,
          color: "orange.500",
        },
        {
          id: "averageSales",
          title: "Average Revenue per Category",
          subTitle: "Average sales per category",
          value: `$${addCommasInPrices(averageRevenue.toFixed(2))}`,
          icon: FiTrendingUp,
          color: "purple.500",
        },
      ]);

    } catch (error) {
      console.error("Error fetching analytics data:", error);
    } finally{
      setIsLoading(false)
    }
  };
  const MotionBox = motion(Box);
  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedDateRange,merchant,storeId]);
  return (
    <Box
      p={6}
      bg={useColorModeValue("gray.100", "gray.700")}
      borderRadius="md"
      boxShadow="md"
      mb={6}
    >
      <VStack align="start" spacing={0}>
        <HStack gap={2}bg>
          <Text fontSize="xl" color={textColor}>
            Total Category Sales
          </Text>
          <Box backgroundColor={"white"}>
            <DateSelector onDateSelect={handleSelection} />
          </Box>
        </HStack>

        <Text fontSize="sm" color={subTextColor} mb={"4"}>
          Comprehensive overview of sales across all categories.
        </Text>
        <HStack>
          <Text fontSize="4xl" fontWeight="bold" color={textColor}>
            ${addCommasInPrices(parseFloat(totalSales).toFixed(2))}
          </Text>
          <Flex align="center">
            <Icon as={trendIcon} color={trendColor} w={5} h={5} />
            <Text ml={1} fontSize="md" color={trendColor} fontWeight="semibold">
              {weeklyChange === 0 ? "0%" : `${Math.abs(weeklyChange)}%`}{" "}
              {isIncrease ? "↑" : "↓"} this week
            </Text>
          </Flex>
        </HStack>
        <Text fontSize="md" color={subTextColor}>
          Total Categories: {totalCategories}
        </Text>

        <Box
      width="100%"
      px={{ base: 4, md: 6, lg: 8 }} // Responsive horizontal padding
      py={{ base: 4, md: 6, lg: 8 }} // Responsive vertical padding
    >
      {/* Header */}
      {isLoading ? (
        <Skeleton height="24px" mb={{ base: 3, md: 4 }} />
      ) : (
        <MotionBox
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Text
            fontSize={{ base: "xl", md: "2xl", lg: "2xl" }} // Adjusted to prevent excessive scaling
            fontWeight="bold"
            mb={{ base: 3, md: 4 }} // Responsive margin-bottom
          >
            Key Metrics
          </Text>
        </MotionBox>
      )}

      {/* Metrics Grid */}
      {isLoading ? (
        <SimpleGrid
          columns={{ base: 1, sm: 2, md: 3, lg: 4 }} // Added 'sm' breakpoint for smoother scaling
          spacing={{ base: 4, md: 6 }} // Responsive spacing between grid items
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <Box
              key={index}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="md"
              p={{ base: 3, md: 4 }} // Responsive padding inside metric boxes
            >
              <HStack spacing={3} mb={{ base: 2, md: 3 }}>
                <Skeleton circle height="30px" width="30px" />
                <Skeleton height="20px" width="100px" />
              </HStack>
              <Skeleton height="24px" width="80%" />
            </Box>
          ))}
        </SimpleGrid>
      ) : (
        <SimpleGrid
          columns={{ base: 1, sm: 2, md: 3, lg: 4 }} // Responsive columns
          spacing={{ base: 4, md: 6 }}
        >
          {keyMetrics.map((metric) => (
            <MotionBox
              key={metric.id}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="md"
              p={{ base: 3, md: 4 }} // Responsive padding inside metric boxes
              // bg={useColorModeValue("white", "gray.800")} // Maintain existing color modes
              boxShadow="sm" // Optional: Adds subtle shadow for depth
              transition="transform 0.2s, box-shadow 0.2s" // Smooth transition on hover
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "md",
              }} // Optional: Adds interactivity on hover
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              // transition={{ duration: 0.5, delay: metric.id * 0.1 }}
            >
              {/* Metric Header */}
              <HStack spacing={3} mb={{ base: 2, md: 3 }}>
                <Icon
                  as={metric.icon}
                  w={{ base: 5, md: 6 }} // Responsive icon width
                  h={{ base: 5, md: 6 }} // Responsive icon height
                  color={metric.color}
                />
                <Text
                  fontWeight="bold"
                  fontSize={{ base: "md", md: "md" }} // Consistent font size across breakpoints
                  color={textColor}
                >
                  {metric.title}
                </Text>
              </HStack>

              {/* Metric Value */}
              <Text
                fontSize={{ base: "lg", md: "xl" }} // Adjusted to prevent overly large text
                fontWeight="bold"
                mt={{ base: 1, md: 2 }} // Responsive margin-top
                color={textColor}
              >
                {metric.value}
              </Text>
            </MotionBox>
          ))}
        </SimpleGrid>
      )}
    </Box>
      </VStack>
    </Box>
  );
};

// New Reusable CategorySalesRankingCarousel Component with Ranking Numbers
const CategorySalesRankingCarousel = ({ data, rankingBy, onToggleRanking }) => {
  const subTextColor = useColorModeValue("gray.600", "gray.500");
  const cardBg = useColorModeValue("gray.100", "gray.700");
  const cardTextColor = useColorModeValue("gray.800", "white");

  // Define gradient colors for rankings
  const getRankGradient = (rank) => {
    switch (rank) {
      case 1:
        return "linear(to-r, yellow.400, yellow.600)";
      case 2:
        return "linear(to-r, gray.300, gray.500)";
      case 3:
        return "linear(to-r, orange.400, orange.600)";
      default:
        return "linear(to-r, gray.400, gray.600)";
    }
  };

  return (
    <Box mt={8}>
      <Flex alignItems={"center"} justifyContent={"space-between"}>
        <Box mr={"4"}>
          <Text fontSize="2xl" fontWeight="bold">
            Category Sales Ranking
          </Text>
          <Text fontSize="sm" color={subTextColor} mb={4}>
            Categories ranked by{" "}
            {rankingBy === "revenue" ? "revenue" : "units sold"}.
          </Text>
        </Box>
        <Button size="lg" onClick={onToggleRanking} border="1px" borderColor="gray.300">
          Rank by {rankingBy === "revenue" ? "Units Sold" : "Revenue"}
        </Button>
      </Flex>
      <Swiper
        modules={[Navigation, Pagination, Scrollbar, A11y]}
        spaceBetween={20}
        slidesPerView={useBreakpointValue({ base: 1, sm: 2, md: 3, lg: 4 })}
        navigation
        pagination={{ clickable: true }}
        scrollbar={{ draggable: true }}
        style={{ padding: "20px 0" }}
      >
        {data.map((category, index) => (
          <SwiperSlide key={category.id} style={{ padding: "15px" }}>
            <Box
              bg={cardBg}
              borderRadius="lg"
              p={2}
              textAlign="center"
              boxShadow="md"
              height="100%"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              {/* Ranking Number */}
              <Text
                fontSize="3xl"
                fontWeight="bold"
                bgGradient={getRankGradient(index + 1)}
                bgClip="text"
                mb={4}
              >
                {index + 1}
              </Text>
              {/* Category Image */}
              <Image
                src={
                  getMediaURL(category.imageUrl) ||
                  "https://via.placeholder.com/100"
                }
                alt={category.category}
                borderRadius="full"
                boxSize="120px"
                objectFit="cover"
                mb={4}
                fallbackSrc="https://via.placeholder.com/100"
              />
              {/* Category Name */}
              <Text
                fontSize="lg"
                fontWeight="bold"
                color={cardTextColor}
                mb={2}
              >
                {category.category || " - "}
              </Text>
              {/* Revenue or Units Sold */}
              {rankingBy === "revenue" ? (
                <Text fontSize="sm" color={subTextColor}>
                  Revenue: $
                  {addCommasInPrices(
                    parseFloat(category.totalRevenue).toFixed(2)
                  )}
                </Text>
              ) : (
                <Text fontSize="sm" color={subTextColor}>
                  Units Sold: {category.totalItemsSold}
                </Text>
              )}
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
};

// Category Sales Detailed View Component
const CategorySales = () => {
  const [categories, setCategories] = useState([]);
  const [loadingSales, setLoadingSales] = useState(true);
  const [weeklyChange, setWeeklyChange] = useState(0);
  const [rankingBy, setRankingBy] = useState("revenue"); // 'revenue' or 'unitsSold'
  const [merchant, setMerchant] = useState(null);
  const [categorySalesData, setCategorySalesData] = useState(null);
  const [keyMetricsFilter, setKeyMetricsFilter] = useState({});
  const [isFilter, setIsFilter] = useState(false);
  const [metricOption, setMetricOption] = useState("totalRevenue");

  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: moment("2023-01-01"), // Start date is 1st January 2000
    endDate: moment(), // End date is the current date
  });
  const [chartType, setChartType] = useState("line");

  const [selectedFilter, setSelectedFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [storeId, setStoreid] = useState("");

  const navigate = useNavigate(); // Initialize useNavigate
  const user = JSON.parse(localStorage.getItem("user"));

  // Chakra UI color modes
  const textColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const subTextColor = useColorModeValue("gray.600", "gray.400");
  const chartbg = useColorModeValue("gray.100", "gray.700");
  const handleSelection = (selection) => {
    setSelectedDateRange(selection)
  };

  const fetchAnalyticsData = async () => {
    try {
      setIsLoading(true);
      const params1 = {
        filter: selectedFilter,
        store:storeId
      };
      const params2 = {
        startDate:moment(selectedDateRange.startDate)?.format("YYYY-MM-DD"),
        endDate: moment(selectedDateRange.endDate)?.format("YYYY-MM-DD"),
        store:storeId
      };
      const token = localStorage.getItem("access_token");
      const res = await API.get("/analytics/category-sales", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: isFilter ? params1 : params2,
      });

      const categoriesData = res.data.data.categories;
      const totalRevenue =
        categoriesData?.length > 0
          ? categoriesData.reduce(
              (sum, category) => sum + parseFloat(category.totalRevenue),
              0
            )
          : 0;
      const averageRevenue = totalRevenue / categoriesData.length || 0;

      // Find best category by revenue
      const bestCategoryByRevenue =
        categoriesData?.length > 0
          ? categoriesData.reduce((prev, current) =>
              parseFloat(current.totalRevenue) > parseFloat(prev.totalRevenue)
                ? current
                : prev
            )
          : 0;

      // Find best category by units sold
      const bestCategoryByUnitsSold =
        categoriesData?.length > 0
          ? categoriesData.reduce((prev, current) =>
              current.totalItemsSold > prev.totalItemsSold ? current : prev
            )
          : 0;
      setKeyMetricsFilter({
        totalRevenue: totalRevenue.toFixed(2),
        averageRevenue: averageRevenue.toFixed(2),
        bestCategoryByRevenue: parseFloat(
          bestCategoryByRevenue.totalRevenue
        ).toFixed(2),
        bestCategoryByUnitsSold: bestCategoryByUnitsSold.totalItemsSold,
      });
      setCategorySalesData(categoriesData);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching analytics data:", error);
    } finally {
      setLoadingSales(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedFilter, selectedDateRange, isFilter,merchant,storeId]);

  useEffect(() => {
    if (categorySalesData) {
      const categoriesData = categorySalesData;
      // Set categories for chart and ranking
      setCategories(categoriesData);
    }
    setLoadingSales(false);
  }, [categorySalesData]);

  // Toggle ranking between revenue and units sold
  const onToggleRanking = () => {
    setRankingBy(rankingBy === "revenue" ? "unitsSold" : "revenue");
  };

  // Get sorted categories for ranking
  const sortedCategories = useMemo(() => {
    const sorted = [...categories];
    if (rankingBy === "revenue") {
      sorted.sort((a, b) => parseFloat(b.revenue) - parseFloat(a.revenue));
    } else {
      sorted.sort((a, b) => b.itemsSold - a.itemsSold);
    }
    return sorted;
  }, [categories, rankingBy]);

  return (
    <Box p={5} color={textColor}>
      {isLoading ? (
        <Flex justify="center" align="center" minH="100vh">
          <Spinner size="xl" />
        </Flex>
      ) : (
        <VStack spacing={6} align="stretch">
          {/* Heading with Back Button */}
          <Flex justify="flex-start" align="center">
            <IconButton
              icon={<FiArrowLeft />}
              variant="outline"
              color={textColor}
              borderRadius={"full"}
              onClick={() => navigate(-1)} // Navigate back in history
              _hover={{ bg: "gray.200" }}
              aria-label="Go Back to Analytics Dashboard"
              mr={"4"}
              size="lg"
            />
            <VStack align="start" spacing={1} width={"100%"}>
              <HStack
                display={"flex"}
                justifyContent={"space-between"}
                width={"100%"}
              >
                <Heading size="xl" fontWeight="bold">
                  Category Sales
                </Heading>
                <HStack>
                  {user.role === "admin" && (
                  <HStack>
                    <Text>By Merchants</Text>
                    <CustomersDropDown
                      onOptionSelect={(merch) => setMerchant(merch)}
                    />
                  </HStack>
                )}
               <HStack><Text>By Store</Text><StoresDropDown onOptionSelect={(store)=>setStoreid(store.id)} merchantId={merchant?.id} role={user?.role}/></HStack>

                </HStack>
                
              </HStack>
              <Text fontSize="md" color={subTextColor}>
                Analyze sales performance by category
              </Text>
            </VStack>
            {/* Placeholder to balance the flex layout */}
            <Box width="75px" />
          </Flex>

          {/* Sales Summary Header */}
          <CategorySalesSummary
            weeklyChange={weeklyChange}
            borderColor={borderColor}
            merchant={merchant}
            storeId={storeId}
          />

          <Box
            // p={4}
            borderRadius="md"
            height="450px"
            width={"full"}
            bg={chartbg}
            boxShadow="md"
          >
            <VStack>
              <HStack
                padding={4}
                width={"100%"}
                justifyContent={"flex-start"}
                wrap={"wrap"}
              >
                <DateRangePicker
                  selectedDateRange={selectedDateRange}
                  onDateRangeChange={(range) => {
                    setIsFilter(false);
                    setSelectedDateRange(range);
                  }}
                />
                <DateSelector onDateSelect={handleSelection} />
                <KeyMetricsSelector
                  options={[
                    { label: "Revenue", value: "totalRevenue" },
                    { label: "Items Sold", value: "totalItemsSold" },
                  ]}
                  onSelectionChange={(option) => setMetricOption(option)}
                />
                <ChartSelection onChange={(type) => setChartType(type)} />
              </HStack>
              <HStack
                paddingX={4}
                width={"100%"}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                gap={10}
                wrap={"wrap"}
              >
                <Text display={"flex"} alignItems={"center"}>
                  <Icon
                    as={FiTrendingUp}
                    w={5}
                    h={5}
                    color={"green.500"}
                    marginRight={"4px"}
                  />
                  Total :
                  <Text paddingLeft={2} as={"span"}>
                    ${addCommasInPrices(keyMetricsFilter?.totalRevenue)}
                  </Text>
                </Text>
                <Text display={"flex"} alignItems={"center"}>
                  <Icon
                    as={FiTrendingUp}
                    w={5}
                    h={5}
                    color={"blue.500"}
                    marginRight={"4px"}
                  />
                  Best Category by Revenue :
                  <Text paddingLeft={2} as={"span"}>
                    ${addCommasInPrices(keyMetricsFilter?.bestCategoryByRevenue)}
                  </Text>
                </Text>
                <Text display={"flex"} alignItems={"center"}>
                  <Icon
                    as={FiTrendingUp}
                    w={5}
                    h={5}
                    color={"orange.500"}
                    marginRight={"4px"}
                  />
                  Best Category by Units Sold :
                  <Text paddingLeft={2} as={"span"}>
                    {keyMetricsFilter?.bestCategoryByUnitsSold}
                  </Text>
                </Text>
                <Text display={"flex"} alignItems={"center"}>
                  <Icon
                    as={FiTrendingUp}
                    w={5}
                    h={5}
                    color={"purple.500"}
                    marginRight={"4px"}
                  />
                  Average Revenue per Category :
                  <Text paddingLeft={2} as={"span"}>
                    ${addCommasInPrices(keyMetricsFilter?.averageRevenue)}
                  </Text>
                </Text>
              </HStack>
              <Box width={"100%"} height={"380px"}>
                <BarLineChart
                  data={categorySalesData}
                  chartType={chartType}
                  xKey={"category"}
                  yKey={metricOption}
                  valueKey={metricOption}
                />
              </Box>
            </VStack>
          </Box>

          {/* Key Metrics Tiles */}

          {/* Category Sales Ranking Carousel */}
          <CategorySalesRankingCarousel
            data={sortedCategories}
            rankingBy={rankingBy}
            onToggleRanking={onToggleRanking}
          />
        </VStack>
      )}
    </Box>
  );
};

export default CategorySales;
